/* v3.18-1302 — EMAIL-ARCH-003
 *
 * Enterprise Email Template Picker
 *
 * Reusable searchable picker that lets any Compose surface browse the
 * corporate template library, preview a template, and hydrate it via
 * the render endpoint against the current client/deal context.
 *
 * Categories are rendered as expandable sections.  Search, category
 * filter and tag filter are supported.  No hardcoded categories.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { ChevronDown, ChevronRight, Search, Loader2, FileText, X } from 'lucide-react';

export default function EmailTemplatePickerDialog({
  open,
  onOpenChange,
  clientId,
  dealId,
  extraContext = {},
  onSelect,           // ({rendered, template, context}) => void
}) {
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates]   = useState([]);
  const [loading, setLoading]       = useState(false);
  const [query, setQuery]           = useState('');
  const [openCats, setOpenCats]     = useState({});
  const [previewTpl, setPreviewTpl] = useState(null);   // full row w/ body_html fetched separately
  const [previewData, setPreviewData] = useState(null); // rendered payload
  const [rendering, setRendering]   = useState(false);
  const [selectedTag, setSelectedTag] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    (async () => {
      try {
        const [c, t] = await Promise.all([
          api.get('/email-template-library/categories'),
          api.get('/email-template-library/templates?limit=500'),
        ]);
        setCategories(c.data?.items || []);
        setTemplates(t.data?.items || []);
        // Expand the first two categories by default so the picker
        // feels populated instead of empty.
        const initial = {};
        (c.data?.items || []).slice(0, 2).forEach((cat) => { initial[cat.key] = true; });
        setOpenCats(initial);
      } catch (e) {
        if (e?.response?.status === 403) {
          toast.error('You do not have access to the Email Template Library.');
          onOpenChange?.(false);
        } else {
          toast.error(e?.response?.data?.detail || 'Failed to load templates');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [open, onOpenChange]);

  const allTags = useMemo(() => {
    const set = new Set();
    templates.forEach((t) => (t.tags || []).forEach((tag) => set.add(tag)));
    return Array.from(set).sort();
  }, [templates]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return templates.filter((t) => {
      if (selectedTag && !(t.tags || []).includes(selectedTag)) return false;
      if (!q) return true;
      return (
        (t.name || '').toLowerCase().includes(q)     ||
        (t.subject || '').toLowerCase().includes(q)  ||
        (t.body_text || '').toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q)
      );
    });
  }, [templates, query, selectedTag]);

  const grouped = useMemo(() => {
    const m = new Map();
    categories.forEach((cat) => m.set(cat.key, { cat, items: [] }));
    filtered.forEach((t) => {
      const bucket = m.get(t.category_key);
      if (bucket) bucket.items.push(t);
    });
    return Array.from(m.values()).filter((g) => g.items.length > 0 || !query);
  }, [categories, filtered, query]);

  const toggleCat = (key) => setOpenCats((prev) => ({ ...prev, [key]: !prev[key] }));

  const openPreview = useCallback(async (tpl) => {
    setPreviewTpl(tpl);
    setPreviewData(null);
    setRendering(true);
    try {
      const { data } = await api.post(`/email-template-library/templates/${tpl.id}/render`, {
        client_id: clientId || null,
        deal_id:   dealId   || null,
        context:   extraContext || {},
      });
      setPreviewData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to render template');
    } finally {
      setRendering(false);
    }
  }, [clientId, dealId, extraContext]);

  const useTemplate = () => {
    if (!previewData) return;
    onSelect?.(previewData);
    onOpenChange?.(false);
  };

  return (
    <Dialog open={!!open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
        data-testid="email-template-picker-dialog"
      >
        <DialogHeader>
          <DialogTitle>Email Template Library</DialogTitle>
          <DialogDescription>
            Corporate communications library — search, preview, and load a template into Compose &amp; Approve.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-5 gap-4 flex-1 overflow-hidden">
          {/* LEFT — categories + search */}
          <div className="col-span-2 border rounded flex flex-col overflow-hidden">
            <div className="p-2 border-b bg-slate-50 space-y-2">
              <div className="relative">
                <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search name, subject, body…"
                  className="pl-7 h-8 text-sm"
                  data-testid="template-picker-search"
                />
              </div>
              {allTags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {allTags.slice(0, 8).map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setSelectedTag(selectedTag === tag ? '' : tag)}
                      className={`text-[10px] px-1.5 py-0.5 rounded border ${selectedTag === tag ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-300 hover:bg-slate-100'}`}
                      data-testid={`template-picker-tag-${tag}`}
                    >
                      {tag}
                    </button>
                  ))}
                  {selectedTag && (
                    <button type="button" onClick={() => setSelectedTag('')} className="text-[10px] px-1.5 py-0.5 rounded border border-red-300 text-red-700 hover:bg-red-50" data-testid="template-picker-clear-tag">
                      <X size={10} className="inline" /> clear
                    </button>
                  )}
                </div>
              )}
            </div>
            <ScrollArea className="flex-1">
              {loading && (
                <div className="p-3 text-sm text-slate-500 flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Loading library…
                </div>
              )}
              {!loading && grouped.map(({ cat, items }) => (
                <div key={cat.key} className="border-b">
                  <button
                    type="button"
                    onClick={() => toggleCat(cat.key)}
                    className="w-full flex items-center justify-between gap-2 px-2 py-1.5 hover:bg-slate-50 text-left"
                    data-testid={`template-picker-cat-${cat.key}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-1.5 h-4 rounded-sm" style={{ background: cat.color || '#64748b' }} />
                      <span className="text-sm font-medium truncate">{cat.name}</span>
                      <span className="text-[10px] text-slate-500">({items.length})</span>
                    </div>
                    {openCats[cat.key] || query ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  {(openCats[cat.key] || query) && (
                    <div className="pl-4 pb-1">
                      {items.length === 0 ? (
                        <div className="text-xs text-slate-400 italic px-2 py-1">No templates.</div>
                      ) : (
                        items.map((tpl) => (
                          <button
                            key={tpl.id}
                            type="button"
                            onClick={() => openPreview(tpl)}
                            className={`w-full text-left px-2 py-1 rounded text-xs hover:bg-slate-100 flex items-center gap-1 ${previewTpl?.id === tpl.id ? 'bg-slate-100' : ''}`}
                            data-testid={`template-picker-item-${tpl.key}`}
                          >
                            <FileText size={11} className="text-slate-400 shrink-0" />
                            <span className="truncate">{tpl.name}</span>
                            {(tpl.tags || []).slice(0, 1).map((tag) => (
                              <Badge key={tag} variant="secondary" className="text-[9px] px-1 py-0 ml-auto shrink-0">{tag}</Badge>
                            ))}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ))}
              {!loading && grouped.length === 0 && (
                <div className="p-4 text-sm text-slate-500 text-center">No templates match your search.</div>
              )}
            </ScrollArea>
          </div>

          {/* RIGHT — preview */}
          <div className="col-span-3 border rounded flex flex-col overflow-hidden">
            {!previewTpl ? (
              <div className="flex-1 flex items-center justify-center text-sm text-slate-400 p-4">
                Select a template to preview.
              </div>
            ) : (
              <>
                <div className="p-3 border-b bg-slate-50">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate" data-testid="template-preview-name">{previewTpl.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{previewTpl.key} · v{previewTpl.version || 1}</div>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {(previewTpl.tags || []).map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-[10px]">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                  {previewTpl.description && (
                    <div className="text-[11px] text-slate-500 mt-1">{previewTpl.description}</div>
                  )}
                </div>
                <ScrollArea className="flex-1">
                  {rendering ? (
                    <div className="p-4 text-sm text-slate-500 flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin" /> Rendering…
                    </div>
                  ) : previewData ? (
                    <div className="p-3 space-y-3">
                      <div>
                        <div className="text-[10px] uppercase text-slate-500 mb-1">Subject</div>
                        <div className="text-sm border rounded px-2 py-1 bg-white" data-testid="template-preview-subject">
                          {previewData.rendered.subject}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase text-slate-500 mb-1">Body</div>
                        <div className="border rounded p-3 bg-white text-sm whitespace-pre-wrap" data-testid="template-preview-body">
                          {previewData.rendered.body_text}
                        </div>
                      </div>
                      {previewData.template?.merge_fields?.length > 0 && (
                        <div>
                          <div className="text-[10px] uppercase text-slate-500 mb-1">Merge fields used</div>
                          <div className="flex flex-wrap gap-1">
                            {previewData.template.merge_fields.map((f) => (
                              <Badge key={f} variant="outline" className="text-[10px]">{f}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </ScrollArea>
              </>
            )}
          </div>
        </div>

        <DialogFooter className="flex-row items-center justify-between gap-2">
          <div className="text-[11px] text-slate-500">
            {templates.length > 0 && `${templates.length} template${templates.length === 1 ? '' : 's'} · ${categories.length} categor${categories.length === 1 ? 'y' : 'ies'}`}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)} data-testid="template-picker-cancel">Cancel</Button>
            <Button type="button" onClick={useTemplate} disabled={!previewData} data-testid="template-picker-use">
              Use Template
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
